import ReactEcharts from 'echarts-for-react';
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import { gaugeChartFormatter } from '../ChartOptions/Format/NumberFormatter';
import { ChartsMapStateToProps, ChartsReduxStateProps } from './ChartsCommonInterfaces';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { tooltipStyle } from '../Common/EChartTooltip';

import { palette } from '../..';

import EmptyQuery from './EmptyQuery';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { changingValuesofSteps } from '../../redux/ChartPoperties/ChartControlsActions';
import { useDispatch } from 'react-redux';
import { shouldChartDisplayWithSecurity } from '../CommonFunctions/CommonFunctions';
import { AXIS_THRESHOLDS, LABEL_THRESHOLDS } from '../ChartOptions/ChartThresholds';

const GaugeChart = ({
  //props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  fontFamily,
  softUI,

  //state
  chartControls,
  chartProperties,
}: ChartsReduxStateProps) => {
  const chartControl: ChartControlsProps = chartControls.properties[propKey];
  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];

  const { tooltipFormats, chartAxes, isTooltipEnabled, gaugeChartFormats, chartType } = useSelector(
    (state: RootState) => ({
      tooltipFormats: state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats,
      chartAxes: state.chartProperties?.properties[propKey]?.chartAxes,
      isTooltipEnabled:
        state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
      gaugeChartFormats: state.chartControls?.properties[propKey]?.formatOptions?.gaugeChartFormats,
      chartType: state.chartProperties?.properties[propKey]?.chartType,
    }),
  );

  const [gaugeChartData, setGaugeChartData] = useState<any>([]);

  let colorArray: any[] = [];
  const allThemes = useCombinedColorSchemes();

  const chartThemes = allThemes.filter((el) => {
    if (
      !chartControl.colorScheme ||
      (chartArea === 'dashboard' &&
        !chartControls.properties[propKey].axisOptions.gaugeChartControls.manualColor)
    ) {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];

  const getColors = () => {
    const steps = chartControl.axisOptions.gaugeChartControls.stepcolor;

    // Reset
    colorArray = [];

    let previousPer = -1; // Track the previous percentage

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      let colorToUse;
      if (chartControl.axisOptions.gaugeChartControls.manualColor) {
        colorToUse = step.color;
      } else {
        const fallbackColors = selectedTheme?.colors || [];
        colorToUse =
          fallbackColors.length > 0 ? fallbackColors[i % fallbackColors.length] : step.color;
      }

      //  Percentage
      let safePer = Number(step.per);
      if (isNaN(safePer)) safePer = 0;
      if (safePer < 0) safePer = 0;
      if (safePer > 1) safePer = 1;

      //  Ensure Strict Ascending Order
      // If this step's percentage is less than or equal to the previous step's percentage,
      // We explicitly allow 0 for the first step if needed, but usually stops are > 0.
      if (i > 0 && safePer <= previousPer) {
        safePer = previousPer + 0.0001;
        // Clamp again just in case we went over 1
        if (safePer > 1) safePer = 1;
      }

      // Force last step to exactly 1
      if (i === steps.length - 1) {
        safePer = 1;
      }
      previousPer = safePer;

      colorArray.push([safePer, colorToUse]);
    }
  };
  getColors();

  useEffect(() => {
    if (chartData.length >= 1) {
      setGaugeChartData([
        {
          name: Object.keys(chartData[0])[0],
          value: Object.values(chartData[0])[0],
        },
      ]);
    }
  }, [chartData]);

  const dispatch = useDispatch();

  //-----------change default color set for steps according to the selected theme--------//
  // useEffect(() => {
  //   const stepsWithUserSelectedColor = chartControls.properties[
  //     propKey
  //   ].axisOptions.gaugeChartControls.stepcolor.map((element, index) => {
  //     element.color = colorArray[index][1];
  //     return element;
  //   });
  //   dispatch(changingValuesofSteps(propKey, stepsWithUserSelectedColor as any));
  // }, [selectedTheme]);

  const getResponsiveSettings = () => {
    const width = Math.floor(graphDimension.width);
    const height = Math.floor(graphDimension.height);
    const size = Math.min(width, height); // Use the smaller dimension to gauge size

    let settings = {
      detailFontSize: (chartControl.labelOptions.fontSize || 12) * 2.5,
      titleFontSize: 16,
      showTitle: true,
      showAxisLabels: chartControl.axisOptions.gaugeAxisOptions.showAxisLabel,
      showTicks: chartControl.axisOptions.gaugeAxisOptions.showTick,
      axisLineWidth: 10,
      labelDistance: chartControl.axisOptions.gaugeAxisOptions.labelPadding,
      pointerWidth: 8,
      pointerLength: '75%',
    };

    if (size < 260) {
      settings.detailFontSize = Math.round(settings.detailFontSize * (18 / 30));
      settings.titleFontSize = 12;
      settings.axisLineWidth = 8;
      settings.labelDistance = 15;
      settings.pointerWidth = 5;
      settings.pointerLength = '70%';
    }

    if (size < LABEL_THRESHOLDS.gauge.min) {
      settings.detailFontSize = Math.round(settings.detailFontSize * (16 / 30));
      settings.titleFontSize = 10;
      settings.showAxisLabels = false; // Hide axis labels
      settings.showTitle = false;
      settings.showTicks = false; // Hide ticks
      settings.pointerLength = '55%';
    }

    if (size < AXIS_THRESHOLDS.common.h + 30) {
      // 180 = 150 + 30 preserved consistency
      settings.detailFontSize = Math.round(settings.detailFontSize * (12 / 30));
      settings.titleFontSize = 8;
      settings.axisLineWidth = 6;
      settings.pointerWidth = 5;
      settings.pointerLength = '40%';
    }

    return settings;
  };

  const responsiveSettings = getResponsiveSettings();

  const RenderChart = () => {
    return (
      <ReactEcharts
        // theme={chartControl.colorScheme}
        style={{
          //  padding: '1rem',
          //paddingBottom: '0px',
          paddingTop: '3px',
          width: graphDimension.width,
          height: graphDimension.height + 5,
          overflow: 'hidden',
          margin: 'auto',
          border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
        }}
        option={{
          color: selectedTheme?.colors || [],
          backgroundColor: selectedTheme?.background || [],
          // animation: chartArea ? false : true, // Turning animation off for current release, to be added later on
          animation: false,
          legend: {
            // textStyle :{color : getContrastColor(chartThemes[0].background)},
            type: 'scroll',
            show: chartControl.legendOptions?.showLegend,
            itemHeight:
              chartArea === 'dashboard'
                ? chartControl.legendOptions?.symbolHeight / 2
                : chartControl.legendOptions?.symbolHeight,
            itemWidth:
              chartArea === 'dashboard'
                ? chartControl.legendOptions?.symbolWidth / 2
                : chartControl.legendOptions?.symbolWidth,
            itemGap: chartControl.legendOptions?.itemGap,

            left: chartControl.legendOptions?.position?.left,
            top: chartControl.legendOptions?.position?.top,
            orient: chartControl.legendOptions?.orientation,
            textStyle: {
              color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
              fontFamily: fontFamily,
            },
          },
          tooltip: {
            show: isTooltipEnabled,
            useHTML: true,
            trigger: 'item',
            backgroundColor: 'white',
            borderWidth: 1,
            padding: 0,
            textStyle: {
              color: '#333',
              fontSize: 16,
            },
            appendToBody: true,
            extraCssText:
              'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word;',
            formatter: function (params: any) {
              const _data = { [params.data.name]: params.data.value };
              let mergedData = { ..._data };
              //-- send extra responses in _data so that it maps and shows all key value pairs including extra measure (if any)--//
              const extraResponses = tooltipFormats?.extraMeasureFieldsResponses[0];
              if (tooltipFormats && extraResponses) {
                //---------send final response in tooltip data i.e checked measure plus extra fields-------//
                mergedData = { ...mergedData, ...extraResponses };
              }
              return tooltipStyle(
                params.color,
                mergedData,
                tooltipFormats,
                chartAxes,
                undefined,
                undefined,
                undefined,
                fontFamily,
              );
            },
          },

          series: [
            {
              type: 'gauge',
              radius: chartControl.chartMargin.gaugeRadius + '%',
              // Inside RenderChart -> option -> series

              max: gaugeChartData[0]
                ? chartControl.axisOptions.gaugeAxisOptions.isMaxAuto
                  ? (gaugeChartData[0].value || 0) * 2 // Handle null/undefined data value
                  : isNaN(chartControl.axisOptions.gaugeAxisOptions.max) ||
                      chartControl.axisOptions.gaugeAxisOptions.max === 0
                    ? 100 // Fallback if max is broken/zero
                    : chartControl.axisOptions.gaugeAxisOptions.max
                : 100, // Default to 100 if no data

              min: chartControl.axisOptions.gaugeAxisOptions.min,

              data: gaugeChartData,

              axisLine: {
                lineStyle: {
                  width: responsiveSettings.axisLineWidth,
                  color: [...colorArray],
                  ...(softUI
                    ? {
                        shadowBlur: 10,
                        shadowColor: 'rgba(0, 0, 0, 0.5)',
                        shadowOffsetX: 3,
                        shadowOffsetY: 3,
                      }
                    : {}),
                },

                roundCap: true,
              },
              pointer: {
                width: responsiveSettings.pointerWidth,
                length: responsiveSettings.pointerLength,
                itemStyle: {
                  color: 'auto',
                },
              },
              startAngle: chartControl.axisOptions.gaugeAxisOptions.startAngle,
              endAngle: chartControl.axisOptions.gaugeAxisOptions.endAngle,
              axisTick: {
                show: responsiveSettings.showTicks,
                length: chartControl.axisOptions.gaugeAxisOptions.tickSize,
                distance: chartControl.axisOptions.gaugeAxisOptions.tickPadding,
                lineStyle: {
                  color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
                },
              },
              splitLine: {
                lineStyle: {
                  color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
                },
              },
              title: {
                color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
                fontSize: responsiveSettings.titleFontSize,
                fontFamily: fontFamily,
                show: responsiveSettings.showTitle,
              },
              detail: {
                /* holds the value that display in the center of the gauge*/
                show: chartControl.labelOptions.showLabel,
                width: Math.max(100, responsiveSettings.detailFontSize * 2.5),
                height: Math.max(40, responsiveSettings.detailFontSize * 1.2),
                fontSize: responsiveSettings.detailFontSize,
                fontFamily: fontFamily,
                color: chartControl.labelOptions.labelColor
                  ? chartControl.labelOptions.labelColor
                  : selectedTheme?.dark
                    ? '#ffffff'
                    : '#757474',
                backgroundColor: chartControl.labelOptions.labelBackground
                  ? chartControl.labelOptions.labelBackgroundColor
                  : 'transparent',
                borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
                padding: chartControl.labelOptions.labelBackground
                  ? [
                      Math.round(responsiveSettings.detailFontSize * 0.15),
                      Math.round(responsiveSettings.detailFontSize * 0.2),
                      Math.round(responsiveSettings.detailFontSize * 0.1),
                      Math.round(responsiveSettings.detailFontSize * 0.2),
                    ]
                  : 0,
                formatter: (value: number) => {
                  if (value === null || value === undefined || isNaN(value)) {
                    return '(Blank)';
                  }
                  var formattedValue = value;
                  formattedValue = gaugeChartFormatter(gaugeChartFormats, 'measureFormat', value);
                  return formattedValue;
                },
              },

              axisLabel: {
                show: responsiveSettings.showAxisLabels,
                distance: responsiveSettings.labelDistance,
                color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
                /* holds the value that display when user mouseover on the needle of chart (or the labels inside gauge chart)*/
                fontFamily: fontFamily,
                formatter: (value: number) => {
                  var formattedValue = value;
                  formattedValue = gaugeChartFormatter(gaugeChartFormats, 'labelFormat', value);
                  return formattedValue;
                },
              },
            },
          ],
        }}
      />
    );
  };

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
    chartData.length < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : (
    <RenderChart />
  );
};

const mapStateToProps = (state: ChartsMapStateToProps, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
  };
};

export default connect(mapStateToProps, null)(GaugeChart);
